/**
 * KAIRAVFASHION - Luxury Theme JavaScript Engine
 * Vanilla JS, lightweight, responsive, accessible.
 * Supports Dark & Light mode switcher, sliding cart drawer, variant switching, modals, and pincode.
 */

document.addEventListener('DOMContentLoaded', () => {
  initThemeSwitcher();
  initStickyHeader();
  initMobileNav();
  initCartDrawer();
  initProductPage();
  initModals();
  initPincodeChecker();
  initWishlistToggles();
  initFileUploadDropzone();
});

/* ============================================================
   0. THEME SWITCHER (DARK / LIGHT MODE)
   ============================================================ */
function initThemeSwitcher() {
  const toggleBtns = document.querySelectorAll('[data-theme-toggle]');
  
  const getStoredTheme = () => {
    return localStorage.getItem('kf-theme-mode') || document.documentElement.getAttribute('data-theme') || 'dark';
  };

  const applyTheme = (theme) => {
    let resolvedTheme = theme;
    if (theme === 'system') {
      resolvedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    localStorage.setItem('kf-theme-mode', theme);
  };

  toggleBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const current = document.documentElement.getAttribute('data-theme') || 'dark';
      const nextTheme = current === 'dark' ? 'light' : 'dark';
      applyTheme(nextTheme);
    });
  });

  // Listen for system theme changes if set to system
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (localStorage.getItem('kf-theme-mode') === 'system') {
      applyTheme(e.matches ? 'dark' : 'light');
    }
  });
}

/* ============================================================
   1. STICKY HEADER
   ============================================================ */
function initStickyHeader() {
  const headerWrap = document.querySelector('.site-header-wrapper');
  if (!headerWrap) return;

  const handleScroll = () => {
    if (window.scrollY > 40) {
      headerWrap.classList.add('scrolled');
    } else {
      headerWrap.classList.remove('scrolled');
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();
}

/* ============================================================
   2. MOBILE NAVIGATION DRAWER
   ============================================================ */
function initMobileNav() {
  const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
  const drawer = document.querySelector('[data-mobile-nav-drawer]');
  const closeBtns = document.querySelectorAll('[data-mobile-nav-close]');
  if (!toggleBtn || !drawer) return;

  const openMenu = () => {
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const closeMenu = () => {
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  toggleBtn.addEventListener('click', openMenu);
  closeBtns.forEach(btn => btn.addEventListener('click', closeMenu));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) {
      closeMenu();
    }
  });
}

/* ============================================================
   3. CART DRAWER & FREE SHIPPING CALCULATOR
   ============================================================ */
function initCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  const openTriggers = document.querySelectorAll('[data-cart-open]');
  const closeTriggers = document.querySelectorAll('[data-cart-close]');

  const openCart = () => {
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const closeCart = () => {
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  openTriggers.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openCart();
    });
  });

  closeTriggers.forEach(btn => {
    btn.addEventListener('click', closeCart);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) {
      closeCart();
    }
  });

  // Global Cart Event Listener for Quick Add
  document.addEventListener('click', async (e) => {
    const quickAddBtn = e.target.closest('[data-quick-add]');
    if (quickAddBtn) {
      const variantId = quickAddBtn.dataset.variantId;
      if (!variantId) return;

      quickAddBtn.disabled = true;
      const origText = quickAddBtn.innerHTML;
      quickAddBtn.innerHTML = '<span>Adding...</span>';

      try {
        await addItemToCart(variantId, 1);
        await updateCartDrawer();
        openCart();
      } catch (err) {
        console.error('Add to cart failed:', err);
      } finally {
        quickAddBtn.disabled = false;
        quickAddBtn.innerHTML = origText;
      }
    }

    // Line item quantity stepper inside cart drawer
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      const lineKey = qtyBtn.dataset.key;
      const change = parseInt(qtyBtn.dataset.qtyChange, 10);
      const row = qtyBtn.closest('.cart-item-row');
      const valEl = row ? row.querySelector('[data-qty-val]') : null;
      if (!valEl || !lineKey) return;

      const currentQty = parseInt(valEl.textContent, 10) || 1;
      const newQty = Math.max(0, currentQty + change);

      await updateItemQuantity(lineKey, newQty);
    }

    // Line item removal
    const removeBtn = e.target.closest('[data-remove-key]');
    if (removeBtn) {
      const lineKey = removeBtn.dataset.removeKey;
      if (lineKey) {
        await updateItemQuantity(lineKey, 0);
      }
    }
  });
}

function formatMoney(cents) {
  const amount = (cents / 100).toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  });
  return '₹' + amount;
}

async function updateCartDrawer() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    const cart = await res.json();
    renderCartState(cart);
  } catch (e) {
    console.log('Cart fetch notice:', e);
  }
}

function renderCartState(cart) {
  const countBadges = document.querySelectorAll('[data-cart-count], .cart-bubble-count');
  countBadges.forEach(b => {
    b.textContent = b.classList.contains('cart-bubble-count') ? cart.item_count : `(${cart.item_count})`;
  });

  const drawer = document.getElementById('cart-drawer');
  if (drawer) {
    const threshold = parseInt(drawer.dataset.threshold, 10) || 99900;
    const progressFill = drawer.querySelector('[data-shipping-progress]');
    const progressMsg = drawer.querySelector('[data-shipping-msg]');

    const percent = Math.min(100, Math.round((cart.total_price / threshold) * 100));
    if (progressFill) progressFill.style.width = `${percent}%`;

    if (progressMsg) {
      if (cart.total_price >= threshold) {
        progressMsg.innerHTML = '<span class="text-success font-medium">✨ Congratulations! You\'ve unlocked FREE SHIPPING</span>';
      } else {
        const remaining = threshold - cart.total_price;
        progressMsg.innerHTML = `<span class="text-muted">You are <strong class="text-gold">${formatMoney(remaining)}</strong> away from <strong>FREE SHIPPING</strong></span>`;
      }
    }

    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }
  }
}

async function addItemToCart(id, quantity = 1) {
  const formData = {
    items: [{ id: parseInt(id, 10), quantity: quantity }]
  };
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData)
  });
  return res.json();
}

async function updateItemQuantity(key, quantity) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
    const cart = await res.json();
    renderCartState(cart);
    window.location.reload();
  } catch (e) {
    console.error('Cart update failed:', e);
  }
}

/* ============================================================
   4. PRODUCT DETAIL PAGE (PDP)
   ============================================================ */
function initProductPage() {
  const pdp = document.querySelector('.product-main-section');
  if (!pdp) return;

  const thumbs = pdp.querySelectorAll('.gallery-thumb-btn');
  const mainImg = pdp.querySelector('.gallery-main-img');

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      const targetSrc = thumb.dataset.imgSrc;
      if (mainImg && targetSrc) {
        mainImg.src = targetSrc;
      }
    });
  });

  const sizePills = pdp.querySelectorAll('.size-pill, .swatch-pill');
  const variantInput = pdp.querySelector('[name="id"]');
  const addToCartBtn = pdp.querySelector('.pdp-add-to-cart');

  sizePills.forEach(pill => {
    pill.addEventListener('click', () => {
      if (pill.classList.contains('disabled')) return;

      const siblingGroup = pill.parentElement.querySelectorAll('button');
      siblingGroup.forEach(p => p.classList.remove('active'));
      pill.classList.add('active');

      const variantId = pill.dataset.variantId;
      if (variantInput && variantId) {
        variantInput.value = variantId;
      }

      const isAvailable = pill.dataset.available === 'true';
      if (addToCartBtn) {
        if (isAvailable) {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'Add to Bag';
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'Sold Out';
        }
      }
    });
  });

  const qtyInput = pdp.querySelector('.pdp-qty-input');
  const minusBtn = pdp.querySelector('[data-pdp-qty-minus]');
  const plusBtn = pdp.querySelector('[data-pdp-qty-plus]');

  if (qtyInput && minusBtn && plusBtn) {
    minusBtn.addEventListener('click', () => {
      let val = parseInt(qtyInput.value, 10) || 1;
      if (val > 1) qtyInput.value = val - 1;
    });
    plusBtn.addEventListener('click', () => {
      let val = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = val + 1;
    });
  }

  const accordionTriggers = pdp.querySelectorAll('.accordion-trigger');
  accordionTriggers.forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.accordion-item');
      if (item) {
        item.classList.toggle('open');
      }
    });
  });
}

/* ============================================================
   5. SIZE GUIDE & SEARCH MODALS
   ============================================================ */
function initModals() {
  const sizeGuideTriggers = document.querySelectorAll('[data-size-guide-trigger]');
  const sizeGuideModal = document.getElementById('size-guide-modal');
  const sizeGuideClose = sizeGuideModal ? sizeGuideModal.querySelectorAll('[data-modal-close]') : [];

  if (sizeGuideModal) {
    sizeGuideTriggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        sizeGuideModal.classList.add('open');
        sizeGuideModal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      });
    });

    sizeGuideClose.forEach(btn => {
      btn.addEventListener('click', () => {
        sizeGuideModal.classList.remove('open');
        sizeGuideModal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
    });

    const unitBtns = sizeGuideModal.querySelectorAll('[data-unit]');
    const tableRows = sizeGuideModal.querySelectorAll('.size-chart-table tbody tr');

    unitBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        unitBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const unit = btn.dataset.unit;

        tableRows.forEach(row => {
          const dataVals = JSON.parse(row.dataset[unit] || '[]');
          if (dataVals.length >= 4) {
            const suffix = unit === 'in' ? '"' : ' cm';
            row.querySelector('.col-bust').textContent = dataVals[0] + suffix;
            row.querySelector('.col-waist').textContent = dataVals[1] + suffix;
            row.querySelector('.col-hip').textContent = dataVals[2] + suffix;
            row.querySelector('.col-length').textContent = dataVals[3] + suffix;
          }
        });
      });
    });
  }

  const searchTriggers = document.querySelectorAll('[data-search-trigger]');
  const searchModal = document.getElementById('search-modal');
  const searchClose = searchModal ? searchModal.querySelectorAll('[data-search-close]') : [];
  const searchInput = searchModal ? searchModal.querySelector('[data-search-input]') : null;

  if (searchModal) {
    searchTriggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        searchModal.classList.add('open');
        searchModal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        if (searchInput) setTimeout(() => searchInput.focus(), 100);
      });
    });

    searchClose.forEach(btn => {
      btn.addEventListener('click', () => {
        searchModal.classList.remove('open');
        searchModal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchModal.classList.contains('open')) {
        searchModal.classList.remove('open');
        searchModal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    });
  }
}

/* ============================================================
   6. PINCODE DELIVERY ESTIMATOR
   ============================================================ */
function initPincodeChecker() {
  const widget = document.querySelector('[data-pincode-checker]');
  if (!widget) return;

  const input = widget.querySelector('.pincode-input');
  const btn = widget.querySelector('[data-pincode-btn]');
  const resultBox = widget.querySelector('[data-pincode-result]');
  const errorBox = widget.querySelector('[data-pincode-error]');
  const pincodeVal = widget.querySelector('[data-pincode-val]');
  const dateVal = widget.querySelector('[data-delivery-date]');

  const checkPincode = () => {
    const pin = (input.value || '').trim();
    if (!/^[1-9][0-9]{5}$/.test(pin)) {
      if (errorBox) errorBox.classList.remove('hidden');
      if (resultBox) resultBox.classList.add('hidden');
      return;
    }

    if (errorBox) errorBox.classList.add('hidden');
    if (pincodeVal) pincodeVal.textContent = pin;

    const today = new Date();
    const estDate = new Date();
    estDate.setDate(today.getDate() + 4);
    const dateStr = estDate.toLocaleDateString('en-IN', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });

    if (dateVal) dateVal.textContent = dateStr;
    if (resultBox) resultBox.classList.remove('hidden');
  };

  if (btn) btn.addEventListener('click', checkPincode);
  if (input) {
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        checkPincode();
      }
    });
  }
}

/* ============================================================
   7. WISHLIST UI TOGGLE & NOTIFICATIONS
   ============================================================ */
function showToast(message, icon = '✦') {
  let toast = document.querySelector('.kf-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'kf-toast';
    document.body.appendChild(toast);
  }
  toast.innerHTML = `<span class="text-gold" style="font-size:1.1rem;">${icon}</span> <span>${message}</span>`;
  toast.classList.add('show');
  
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => {
    toast.classList.remove('show');
  }, 2800);
}

function initWishlistToggles() {
  const PRODUCT_FALLBACKS = {
    '101': { title: 'Royal Chanderi Silk Anarkali Kurti', price: '₹2,899', url: '/products/royal-chanderi-silk-anarkali-kurti', image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=800&q=80' },
    '102': { title: 'Gulbagh Handblock Printed Straight Kurti', price: '₹1,699', url: '/products/gulbagh-handblock-printed-straight-kurti', image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?auto=format&fit=crop&w=800&q=80' },
    '103': { title: 'Noor Zari Embroidered Festive Kurti', price: '₹3,299', url: '/products/noor-zari-embroidered-festive-kurti', image: 'https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?auto=format&fit=crop&w=800&q=80' },
    '104': { title: 'Sitara Sequin Embroidered A-Line Kurti', price: '₹2,199', url: '/products/sitara-sequin-embroidered-a-line-kurti', image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=800&q=80' }
  };

  const getWishlist = () => {
    try {
      const raw = JSON.parse(localStorage.getItem('kf-wishlist') || '[]');
      return raw.map(item => {
        if (typeof item === 'object') {
          // If title is just an ID number, resolve with known fallback if possible
          if (/^\d+$/.test(item.title) && PRODUCT_FALLBACKS[item.id]) {
            return { ...PRODUCT_FALLBACKS[item.id], id: String(item.id) };
          }
          return item;
        }
        const fallback = PRODUCT_FALLBACKS[String(item)];
        if (fallback) {
          return { id: String(item), ...fallback };
        }
        return {
          id: String(item),
          title: 'Handcrafted Kurti',
          price: '₹2,899',
          url: '/collections/kurtis',
          image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=400&q=80'
        };
      });
    } catch {
      return [];
    }
  };

  const saveWishlist = (list) => {
    localStorage.setItem('kf-wishlist', JSON.stringify(list));
    updateWishlistCount(list.length);
  };

  const updateWishlistCount = (count) => {
    const headerBtns = document.querySelectorAll('.wishlist-header-btn');
    headerBtns.forEach(btn => {
      let badge = btn.querySelector('.wishlist-count-badge');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'wishlist-count-badge cart-bubble-count';
          btn.appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) {
        badge.remove();
      }
    });
  };

  const renderWishlistDrawer = () => {
    const list = getWishlist();
    const drawer = document.getElementById('wishlist-drawer');
    if (!drawer) return;

    const countBadge = drawer.querySelector('[data-wishlist-drawer-count]');
    const emptyState = drawer.querySelector('[data-wishlist-empty]');
    const itemsList = drawer.querySelector('[data-wishlist-items-list]');

    if (countBadge) countBadge.textContent = `(${list.length})`;

    if (list.length === 0) {
      if (emptyState) emptyState.classList.remove('hidden');
      if (itemsList) {
        itemsList.classList.add('hidden');
        itemsList.innerHTML = '';
      }
      return;
    }

    if (emptyState) emptyState.classList.add('hidden');
    if (itemsList) {
      itemsList.classList.remove('hidden');
      itemsList.innerHTML = list.map(item => {
        const title = typeof item === 'object' ? item.title : item;
        const img = typeof item === 'object' && item.image ? item.image : 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=400&q=80';
        const price = typeof item === 'object' && item.price ? item.price : '₹2,899';
        const url = typeof item === 'object' && item.url ? item.url : '/collections/kurtis';
        const itemId = typeof item === 'object' ? item.id : item;

        return `
          <div class="cart-item-row" data-wishlist-item-id="${itemId}">
            <div class="cart-item-image-col">
              <img src="${img}" alt="${title}" class="cart-item-img" width="70" height="93">
            </div>
            <div class="cart-item-details-col">
              <div class="cart-item-top">
                <h3 class="cart-item-title font-serif"><a href="${url}" class="hover-gold">${title}</a></h3>
                <button type="button" class="cart-item-remove-btn" data-wishlist-remove="${itemId}" aria-label="Remove from Wishlist">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div class="cart-item-price text-sm text-gold font-medium mt-1">${price}</div>
              <div class="mt-2">
                <a href="${url}" class="btn btn-gold btn-block" style="padding: 0.4rem 0.8rem; font-size: 0.72rem;">
                  View &amp; Select Size
                </a>
              </div>
            </div>
          </div>
        `;
      }).join('');
    }
  };

  const openWishlistDrawer = () => {
    const drawer = document.getElementById('wishlist-drawer');
    if (!drawer) return;
    renderWishlistDrawer();
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const closeWishlistDrawer = () => {
    const drawer = document.getElementById('wishlist-drawer');
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  // Close triggers for wishlist drawer
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-wishlist-close]')) {
      closeWishlistDrawer();
    }
    const removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      const idToRemove = removeBtn.dataset.wishlistRemove;
      let list = getWishlist();
      list = list.filter(item => (typeof item === 'object' ? String(item.id) !== String(idToRemove) : String(item) !== String(idToRemove)));
      saveWishlist(list);
      renderWishlistDrawer();
      // Also update any matching product card active state
      document.querySelectorAll(`[data-wishlist-btn][data-id="${idToRemove}"]`).forEach(b => b.classList.remove('active'));
    }
  });

  // Restore active state from localStorage on page load
  const currentList = getWishlist();
  updateWishlistCount(currentList.length);
  document.querySelectorAll('[data-wishlist-btn][data-id]').forEach(btn => {
    const id = btn.dataset.id;
    if (id && currentList.some(item => (typeof item === 'object' ? String(item.id) === String(id) : String(item) === String(id)))) {
      btn.classList.add('active');
    }
  });

  document.addEventListener('click', (e) => {
    // If user clicked inside wishlist drawer on an anchor link (like "View & Select Size"), let it navigate!
    const wishlistLink = e.target.closest('[data-wishlist-drawer-link], [data-wishlist-items-list] a');
    if (wishlistLink) {
      // Allow standard link navigation, just close the drawer
      closeWishlistDrawer();
      return;
    }

    const wishlistBtn = e.target.closest('[data-wishlist-btn]');
    if (!wishlistBtn) return;
    
    e.preventDefault();
    e.stopPropagation();

    // If header wishlist icon clicked -> Open sliding Wishlist Drawer!
    if (wishlistBtn.classList.contains('wishlist-header-btn')) {
      openWishlistDrawer();
      return;
    }

    const card = wishlistBtn.closest('.product-card');
    const pdp = wishlistBtn.closest('.product-main-section');

    let title = 'Handcrafted Kurti';
    let id = wishlistBtn.dataset.id || (card ? card.dataset.productId : null) || 'kurti';
    let img = 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=400&q=80';
    let price = '₹2,899';
    let url = '/collections/kurtis';

    if (card) {
      const titleLink = card.querySelector('.product-card-title a');
      if (titleLink && titleLink.textContent.trim()) {
        title = titleLink.textContent.trim();
        if (titleLink.getAttribute('href')) url = titleLink.getAttribute('href');
      }
      const imgEl = card.querySelector('.product-card-image');
      if (imgEl && imgEl.getAttribute('src')) img = imgEl.getAttribute('src');
      const priceEl = card.querySelector('.price-regular');
      if (priceEl && priceEl.textContent.trim()) price = priceEl.textContent.trim();
    } else if (pdp) {
      const titleEl = pdp.querySelector('.pdp-title');
      if (titleEl) title = titleEl.textContent.trim();
      const mainImg = pdp.querySelector('.gallery-main-img');
      if (mainImg) img = mainImg.src;
      const priceEl = pdp.querySelector('.pdp-price');
      if (priceEl) price = priceEl.textContent.trim();
      url = window.location.pathname;
    }

    let list = getWishlist();
    const existingIndex = list.findIndex(item => (typeof item === 'object' ? String(item.id) === String(id) : String(item) === String(id)));

    if (wishlistBtn.classList.contains('active') || existingIndex !== -1) {
      wishlistBtn.classList.remove('active');
      if (existingIndex !== -1) list.splice(existingIndex, 1);
      saveWishlist(list);
      showToast(`Removed "${title}" from Wishlist`, '♡');
    } else {
      wishlistBtn.classList.add('active');
      list.push({ id: String(id), title: title, image: img, price: price, url: url });
      saveWishlist(list);
      showToast(`Added "${title}" to Wishlist`, '♥');
    }
  });
}

/* ============================================================
   8. FILE & VIDEO UPLOAD DROPZONE (FOR DEFECTIVE CLAIMS)
   ============================================================ */
function initFileUploadDropzone() {
  const dropzones = document.querySelectorAll('[data-file-dropzone]');
  dropzones.forEach(zone => {
    const input = zone.querySelector('[data-file-input]');
    const previewList = zone.querySelector('[data-file-preview-list]');
    if (!input || !previewList) return;

    const handleFiles = (files) => {
      previewList.innerHTML = '';
      if (!files || files.length === 0) {
        previewList.classList.add('hidden');
        return;
      }
      previewList.classList.remove('hidden');

      Array.from(files).forEach((file, index) => {
        const item = document.createElement('div');
        item.className = 'file-preview-item';
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        const icon = isVideo ? '📹' : isImage ? '🖼️' : '📎';
        const sizeKb = Math.round(file.size / 1024);
        const sizeStr = sizeKb > 1024 ? `${(sizeKb / 1024).toFixed(1)}MB` : `${sizeKb}KB`;
        
        item.innerHTML = `<span>${icon}</span> <span class="font-medium" style="max-width:140px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${file.name}</span> <span class="text-gold">(${sizeStr})</span>`;
        previewList.appendChild(item);
      });

      showToast(`Selected ${files.length} file(s) for verification.`, '📎');
    };

    input.addEventListener('change', (e) => {
      handleFiles(e.target.files);
    });

    ['dragenter', 'dragover'].forEach(eventName => {
      zone.addEventListener(eventName, (e) => {
        e.preventDefault();
        zone.classList.add('dragover');
      }, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
      zone.addEventListener(eventName, (e) => {
        e.preventDefault();
        zone.classList.remove('dragover');
      }, false);
    });

    zone.addEventListener('drop', (e) => {
      const dt = e.dataTransfer;
      const files = dt.files;
      input.files = files;
      handleFiles(files);
    }, false);
  });
}
